<template>
  <div>
    <div class="ma-3 allocation-description">
      Allocate the funds from your exchange wallets to a selection of markets
      with {{ baseCurrency }} as quote currency
    </div>

    <p class="headline text-xs-center">
      Total target allocation: {{ bigSliderValue | roundNumber(1) }}%
    </p>
    <v-slider
      :value="bigSliderValue"
      readonly
      class="px-3 master-slider"
      :style="{
        '--allocation-red-pct': `${currentSellsNoSurplusPct}%`,
        '--allocation-blue-pct': `${currentSellsPct +
          currentSellsNoSurplusPct}%`,
        '--allocation-green-pct': `${currentSellsPct +
          currentSellsNoSurplusPct +
          currentBuysPct}%`
      }"
    >
    </v-slider>

    <p class="text-xs-center mb-64">
      <span class="desc">
        <img
          :src="
            '/img/reserved_for_selling' + (themeDark ? '_dark' : '') + '.png'
          "
          width="20"
          height="20"
          class="desc-icon-first"
        />
        <img
          :src="
            '/img/reserved_for_selling_blue' +
              (themeDark ? '_dark' : '') +
              '.png'
          "
          width="20"
          height="20"
          class="desc-icon"
        />
        Reserved for selling
        <v-icon
          class="vertical-align-middle icon-for-information"
          style="font-size: 16px; margin-bottom: 1px;"
          @click.stop.prevent
          @click="
            () => {
              infoAlert(
                $t('information.reserved_for_selling.title'),
                $t('information.reserved_for_selling.description')
              );
            }
          "
          >fa-info-circle</v-icon
        >
      </span>
      <br v-if="windowWidth < 475" />
      <span class="between-icons desc">
        <img
          :src="'/img/in_open_buy_orders' + (themeDark ? '_dark' : '') + '.png'"
          width="20"
          height="20"
          class="desc-icon"
        />
        In open buy orders
      </span>
    </p>

    <v-data-table
      :headers="headers"
      disable-initial-sort
      :custom-sort="customTableSort"
      :pagination.sync="pagination"
      :items="
        markets.filter(
          market =>
            market.enabled ||
            (market.hasOwnProperty('has_allocations') &&
              market.has_allocations === true)
        )
      "
      item-key="market_name"
      :rows-per-page-items="[10, 25, 50, { text: 'All', value: -1 }]"
    >
      <template slot="headers" slot-scope="props">
        <tr>
          <th
            v-for="header in props.headers"
            :key="header.text"
            :class="[
              'column sortable text-xs-left',
              pagination.descending ? 'desc' : 'asc',
              header.value === pagination.sortBy ? 'active' : ''
            ]"
            :aria-sort="pagination.descending ? 'descending' : 'ascending'"
            @click="header.sortable ? changeSort(header) : ''"
          >
            {{ header.text }}
            <v-icon v-if="header.sortable" small>arrow_upward</v-icon>
            <v-icon
              v-if="header.text == 'Target allocation (%)'"
              class="vertical-align-middle icon-for-information"
              style="font-size: 14px; margin-left: 4px; opacity: 1; transform: none !important; -webkit-transform: none !important;"
              @click="
                () => {
                  infoAlert(
                    $t('information.markets_target_allocation.title'),
                    $t('information.markets_target_allocation.description')
                  );
                }
              "
              >fa-info-circle</v-icon
            >
            <v-icon
              v-if="header.text == 'Enable/disable'"
              class="vertical-align-middle icon-for-information"
              style="font-size: 14px; margin-left: 4px; opacity: 1; transform: none !important; -webkit-transform: none !important;"
              @click="
                () => {
                  infoAlert(
                    $t('information.market_enable_disable.title'),
                    $t('information.market_enable_disable.description')
                  );
                }
              "
              >fa-info-circle</v-icon
            >
            <v-icon
              v-if="header.text == 'Add/remove'"
              class="vertical-align-middle icon-for-information"
              style="font-size: 14px; margin-left: 4px; opacity: 1; transform: none !important; -webkit-transform: none !important;"
              @click="
                () => {
                  infoAlert(
                    $t('information.market_add_remove.title'),
                    $t('information.market_add_remove.description')
                  );
                }
              "
              >fa-info-circle</v-icon
            >
          </th>
        </tr>
      </template>
      <template slot="items" slot-scope="props">
        <tr
          v-if="isMobile"
          :class="props.item.enabled ? 'enabled' : 'disabled'"
        >
          <td>
            <div class="subheading mt-4">
              <span v-if="props.item.enabled">
                {{ props.item.target_currency }}/{{ props.item.base_currency }}
              </span>
              <span v-else class="text--disabled">
                {{ props.item.target_currency }}/{{ props.item.base_currency }}
              </span>
            </div>
            <v-slider
              :key="props.item.market_name"
              v-model="props.item.allocation"
              min="0"
              max="100"
              class="child-slider mt-0"
              :error-messages="
                props.item.is_active != 1
                  ? 'Market offline. ' +
                    ($options.filters.isValidDate(props.item.ts_last_active_utc)
                      ? 'Last seen online: ' +
                        $options.filters.formatDate(
                          props.item.ts_last_active_utc,
                          'DD MMM YYYY HH:mm (z).'
                        )
                      : '') +
                    (props.item.exchange_comments &&
                    props.item.exchange_comments.length
                      ? ' Exchange comments: ' + props.item.exchange_comments
                      : '')
                  : ''
              "
              :style="{
                '--allocation-red-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                )}%`,
                '--allocation-blue-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                ) + getSellPct(props.item.exchange, props.item.name)}%`,
                '--allocation-green-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                ) +
                  getSellPct(props.item.exchange, props.item.name) +
                  getBuyPct(props.item.exchange, props.item.name)}%`
              }"
              @input="changedAllocation($event, props.item)"
            ></v-slider>
            <v-layout v-if="windowWidth < 768" wrap>
              <v-flex xs12 lg12 md12>
                <div v-if="props.item.enabled">
                  <p>Percentage:</p>
                  <v-text-field
                    :key="props.item.market_name"
                    :ref="
                      props.item.exchange +
                        props.item.base_currency +
                        props.item.target_currency
                    "
                    v-model="props.item.allocation"
                    type="number"
                    min="0"
                    max="100"
                    :rules="[
                      x =>
                        /(^100$)|^[0-9]\d?$/.test(x) ||
                        $t('settings.invalid_value')
                    ]"
                    :class="[
                      'number-input',
                      props.item.showSaveBtn ? inputChanged : '',
                      props.item.showDataSaved ? showDataSaved : ''
                    ]"
                    @change="changedAllocation($event, props.item)"
                  ></v-text-field>
                </div>
              </v-flex>
              <v-flex xs12 lg12 md12>
                <div class="tr-actions">
                  <v-btn
                    v-if="props.item.enabled && props.item.showSaveBtn"
                    small
                    class="btn-reset"
                    @click="resetMarket($event, props.item)"
                  >
                    Reset
                  </v-btn>
                  <v-btn
                    v-if="
                      props.item.enabled &&
                        props.item.showSaveBtn &&
                        !$refs[
                          props.item.exchange +
                            props.item.base_currency +
                            props.item.target_currency
                        ].hasError
                    "
                    small
                    class="btn-save"
                    @click="saveAllocation($event, props.item)"
                  >
                    Save
                  </v-btn>
                  <v-icon v-if="props.item.showLoadingView">
                    fas fa-circle-notch fa-spin
                  </v-icon>
                </div>
              </v-flex>
              <v-flex xs12 lg12 md12>
                <div v-if="props.item.enabled" style="display: inline-flex">
                  <div>
                    <v-switch
                      :key="props.item.market_name"
                      v-model="props.item.is_activated"
                      style="margin-top: 12px;"
                      @change="
                        changeIsActivated(
                          props.item,
                          props.item.name,
                          props.item.is_activated
                        )
                      "
                    ></v-switch>
                    <span class="toggle-label">{{
                      props.item.is_activated ? `Enabled` : `Disabled`
                    }}</span>
                  </div>
                  <v-btn
                    icon
                    flat
                    primary
                    style="margin-top: 8px;"
                    @click="
                      e =>
                        props.item.enabled && (props.expanded = !props.expanded)
                    "
                  >
                    <v-img
                      :src="
                        '/img/slider_icon_' +
                          (themeDark ? 'dark' : 'light') +
                          '.svg'
                      "
                    ></v-img>
                  </v-btn>
                  <v-btn
                    icon
                    flat
                    small
                    style="margin-top: 12px;"
                    @click="disableMarket(props.item, themeDark)"
                  >
                    <v-img
                      :src="
                        '/img/times_icon_' +
                          (themeDark ? 'dark' : 'light') +
                          '.svg'
                      "
                    ></v-img>
                  </v-btn>
                </div>
                <div v-else>
                  <v-btn
                    v-if="props.item.is_active == 1"
                    flat
                    small
                    icon
                    @click="enableMarket(props.item, themeDark)"
                  >
                    <v-img
                      :src="
                        '/img/check_icon_' +
                          (themeDark ? 'dark' : 'light') +
                          '.svg'
                      "
                    ></v-img>
                  </v-btn>
                  <v-btn
                    icon
                    flat
                    primary
                    @click="e => (props.expanded = !props.expanded)"
                  >
                    <v-img
                      :src="
                        '/img/slider_icon_' +
                          (themeDark ? 'dark' : 'light') +
                          '.svg'
                      "
                    ></v-img>
                  </v-btn>
                </div>
              </v-flex>
            </v-layout>
          </td>
        </tr>
        <tr v-else :class="props.item.enabled ? 'enabled' : 'disabled'">
          <td class="td-slider">
            <div class="subheading mt-4">
              <span v-if="props.item.enabled">
                {{ props.item.target_currency }}/{{ props.item.base_currency }}
              </span>
              <span v-else class="text--disabled">
                {{ props.item.target_currency }}/{{ props.item.base_currency }}
              </span>
            </div>
            <v-slider
              :key="props.item.market_name"
              v-model="props.item.allocation"
              min="0"
              max="100"
              class="child-slider mt-0"
              :error-messages="
                props.item.is_active != 1
                  ? 'Market offline. ' +
                    ($options.filters.isValidDate(props.item.ts_last_active_utc)
                      ? 'Last seen online: ' +
                        $options.filters.formatDate(
                          props.item.ts_last_active_utc,
                          'DD MMM YYYY HH:mm (z).'
                        )
                      : '') +
                    (props.item.exchange_comments &&
                    props.item.exchange_comments.length
                      ? ' Exchange comments: ' + props.item.exchange_comments
                      : '')
                  : ''
              "
              :style="{
                '--allocation-red-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                )}%`,
                '--allocation-blue-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                ) + getSellPct(props.item.exchange, props.item.name)}%`,
                '--allocation-green-pct': `${getSellNoSurplusPct(
                  props.item.exchange,
                  props.item.name
                ) +
                  getSellPct(props.item.exchange, props.item.name) +
                  getBuyPct(props.item.exchange, props.item.name)}%`
              }"
              @input="changedAllocation($event, props.item)"
            ></v-slider>
          </td>
          <td v-if="props.item.enabled">
            <v-text-field
              :ref="
                props.item.exchange +
                  props.item.base_currency +
                  props.item.target_currency
              "
              :key="props.item.market_name"
              v-model="props.item.allocation"
              type="number"
              min="0"
              max="100"
              :rules="[
                x =>
                  /(^100$)|^[0-9]\d?$/.test(x) || $t('settings.invalid_value')
              ]"
              :class="[
                'number-input',
                props.item.showSaveBtn ? inputChanged : '',
                props.item.showDataSaved ? showDataSaved : ''
              ]"
              @change="changedAllocation($event, props.item)"
            ></v-text-field>
          </td>
          <td v-else></td>
          <td class="tr-actions">
            <v-btn
              v-if="props.item.enabled && props.item.showSaveBtn"
              small
              class="btn-reset"
              @click="resetMarket($event, props.item)"
            >
              Reset
            </v-btn>
            <v-btn
              v-if="
                props.item.enabled &&
                  props.item.showSaveBtn &&
                  !$refs[
                    props.item.exchange +
                      props.item.base_currency +
                      props.item.target_currency
                  ].hasError
              "
              small
              class="btn-save"
              @click="saveAllocation($event, props.item)"
            >
              Save
            </v-btn>
            <v-icon v-if="props.item.showLoadingView">
              fas fa-circle-notch fa-spin
            </v-icon>
          </td>
          <td v-if="props.item.enabled">
            <v-btn
              icon
              flat
              primary
              @click="
                e => props.item.enabled && (props.expanded = !props.expanded)
              "
            >
              <v-img
                :src="
                  '/img/slider_icon_' + (themeDark ? 'dark' : 'light') + '.svg'
                "
              ></v-img>
            </v-btn>
          </td>
          <td v-else>
            <v-btn
              icon
              flat
              primary
              @click="e => (props.expanded = !props.expanded)"
            >
              <v-img
                :src="
                  '/img/slider_icon_' + (themeDark ? 'dark' : 'light') + '.svg'
                "
              ></v-img>
            </v-btn>
          </td>
          <td class="text-xs-center">
            <div v-if="props.item.enabled" class="text-xs-center">
              <v-switch
                :key="props.item.market_name"
                v-model="props.item.is_activated"
                class="enable-switch"
                style=""
                @change="
                  changeIsActivated(
                    props.item,
                    props.item.name,
                    props.item.is_activated
                  )
                "
              ></v-switch>
              <span :class="['toggle-label']">
                {{ props.item.is_activated ? `Enabled` : `Disabled` }}
              </span>
            </div>
          </td>
          <td class="text-xs-center">
            <v-btn
              v-if="props.item.enabled"
              icon
              flat
              small
              @click="disableMarket(props.item, themeDark)"
            >
              <v-img
                :src="
                  '/img/times_icon_' + (themeDark ? 'dark' : 'light') + '.svg'
                "
              ></v-img>
            </v-btn>
            <v-btn
              v-if="!props.item.enabled && props.item.is_active == 1"
              flat
              small
              icon
              @click="enableMarket(props.item, themeDark)"
            >
              <v-img
                :src="
                  '/img/check_icon_' + (themeDark ? 'dark' : 'light') + '.svg'
                "
              ></v-img>
            </v-btn>
          </td>
        </tr>
      </template>
      <template slot="expand" slot-scope="props">
        <ActiveMarketsItemAdvanced
          :market="props.item"
          :check-only-disable="true"
        ></ActiveMarketsItemAdvanced>
      </template>
    </v-data-table>
  </div>
</template>

<style scoped>
.mb-64 {
  margin-bottom: 64px !important;
}
.between-icons {
  margin-left: 40px !important;
}
.desc-icon {
  margin-right: 16px;
  margin-bottom: -5px;
}
.desc-icon-first {
  margin-right: 0px !important;
  margin-bottom: -5px;
}
.enable-switch {
  margin-top: 12px;
  display: flex;
  justify-content: center;
}
.td-slider {
  padding-bottom: 8px !important;
}
.child-slider >>> .v-messages__wrapper {
  margin-top: -8px !important;
}
.child-slider >>> .v-slider .v-slider__track__container,
.master-slider >>> .v-slider .v-slider__track__container {
  /*noinspection CssUnresolvedCustomProperty*/
  background: linear-gradient(
    to right,
    #ffc929 0%,
    #ffc929 var(--allocation-red-pct, 0%),
    #3455ce var(--allocation-red-pct, 0%),
    #3455ce var(--allocation-blue-pct, 0%),
    #7ec171 var(--allocation-blue-pct, 0%),
    #7ec171 var(--allocation-green-pct, 0%),
    #f0f2f5 var(--allocation-green-pct, 0%),
    #f0f2f5 100%
  );
  border: 1px solid #424242;
  height: 10px;
  box-shadow: inset 1px 1px 2px rgba(51, 89, 163, 0.24);
}

@media all and (-ms-high-contrast: none), (-ms-high-contrast: active) {
  .child-slider >>> .v-slider .v-slider__track__container,
  .master-slider >>> .v-slider .v-slider__track__container {
    background: #ffffff;
  }
}

.child-slider >>> .v-slider .v-slider__track__container .v-slider__track,
.master-slider >>> .v-slider .v-slider__track__container .v-slider__track {
  background: none !important;
}

.child-slider >>> .v-slider .v-slider__track__container .v-slider__track-fill,
.master-slider >>> .v-slider .v-slider__track__container .v-slider__track-fill {
  background: none !important;
}

.child-slider >>> .v-slider .v-slider__thumb-container .v-slider__thumb,
.master-slider >>> .v-slider .v-slider__thumb-container .v-slider__thumb {
  left: -5px !important;
  border-color: rgba(51, 89, 163, 0.24) !important;
  width: 26px !important;
  height: 26px !important;
  border-radius: 40px !important;
  background-color: #fff !important;
  border: solid 2px rgba(51, 89, 163, 0.24) !important;
  box-shadow: 0px 0.5px 4px rgba(39, 70, 131, 0.2),
    0px 6px 13px rgba(39, 70, 131, 0.2);
}

tr.disabled .child-slider >>> .v-slider .v-slider__thumb-container {
  display: none;
}

tr.disabled {
  background-image: repeating-linear-gradient(
    -45deg,
    transparent,
    transparent 35px,
    rgba(0, 0, 0, 0.025) 35px,
    rgba(0, 0, 0, 0.025) 70px
  ) !important;
}

tr.disabled .child-slider >>> .v-slider .v-slider__track__container {
  /*noinspection CssUnresolvedCustomProperty*/
  background: linear-gradient(
    to right,
    rgba(255, 201, 41, 0.4) 0%,
    rgba(255, 201, 41, 0.4) var(--allocation-red-pct, 0%),
    rgba(52, 85, 206, 0.4) var(--allocation-red-pct, 0%),
    rgba(52, 85, 206, 0.4) var(--allocation-blue-pct, 0%),
    rgba(126, 193, 113, 0.4) var(--allocation-blue-pct, 0%),
    rgba(126, 193, 113, 0.4) var(--allocation-green-pct, 0%),
    rgba(255, 255, 255, 0.4) var(--allocation-green-pct, 0%),
    rgba(255, 255, 255, 0.4) 100%
  );
  border: 1px solid rgba(0, 0, 0, 0.4);
}
>>> .v-slider .v-slider__track__container {
  border-radius: 10px;
  border-color: rgba(51, 89, 163, 0.24) !important;
}
>>> .v-input__slot::before {
  border-style: none !important;
}
>>> .v-text-field.v-input--is-focused
  > .v-input__control
  > .v-input__slot::after {
  -webkit-transform: scaleX(0);
  transform: scaleX(0);
}

.input-changed >>> .v-input__control > .v-input__slot > .v-text-field__slot {
  border: 4px solid #fad35f !important;
}

.input-saved >>> .v-input__control > .v-input__slot > .v-text-field__slot {
  border: 4px solid #7ec171 !important;
}

.tr-actions {
  width: 260px;
}
>>> .theme--light .v-text-field input {
  color: #103a84;
}
.theme--light .toggle-label {
  color: #103a84;
  font-size: 14px;
}
>>> .v-input--selection-controls:not(.v-input--hide-details) .v-input__slot {
  margin-bottom: 0px !important;
}

.btn-reset {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #ffffff !important;
  background: linear-gradient(180deg, #ed4242 0%, #ed4242 100%);
  border-radius: 30px;
  margin: 0px 6px 0px;
  box-shadow: none !important;
}
.btn-save {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: bold;
  font-size: 18px;
  line-height: 22px;
  text-align: center;
  color: #ffffff !important;
  background: linear-gradient(180deg, #85c778 0%, #7ec171 100%);
  border-radius: 30px;
  margin: 0px 6px 0px;
  box-shadow: none !important;
}
>>> .theme--light.v-table thead tr:first-child {
  border-bottom: 1px solid #e5efff;
}
>>> .theme--light.v-table tbody tr:not(:first-child) {
  border-top: 1px solid #e5efff;
}
.theme--light .toggle-title-active {
  color: #103a84;
}
>>> .theme--light
  .v-text-field
  > .v-input__control
  > .v-input__slot
  > .v-text-field__slot {
  background: #f0f2f5;
  box-shadow: inset 1px 1px 3px rgba(51, 89, 163, 0.34);
  border-radius: 5px;
  padding-left: 5px;
  padding-right: 5px;
}

.theme--light span.desc {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #7395d0;
}
.theme--light .allocation-description {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: #7395d0;
  margin-top: 24px;
}
.theme--light .headline {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 22px !important;
  line-height: 26px;
  text-align: center;
  color: #103a84;
  margin-top: 48px;
  margin-bottom: 16px;
}
>>> .theme--light.v-datatable thead th {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  color: #7395d0;
}
>>> .theme--light.v-datatable tbody tr td {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #103a84;
}

>>> .theme--light.v-datatable tbody tr td .v-btn {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #103a84;
}
>>> .theme--light.v-datatable thead th.column.sortable.active,
>>> .theme--light.v-datatable thead th.column.sortable.active .v-icon,
>>> .theme--light.v-datatable thead th.column.sortable:hover {
  color: #103a84;
}
>>> .theme--light .v-datatable__actions {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: #7395d0;
}
>>> .theme--light .v-datatable__actions .v-select__selections {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  color: #103a84 !important;
  max-height: 24px;
}

>>> .theme--light
  .v-datatable__actions
  .v-select__selection.v-select__selection--comma {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  color: #103a84;
  padding-left: 8px;
}

>>> .theme--light .v-datatable__actions .v-input__slot {
  border: 0.8px solid #103a84;
  box-sizing: border-box;
  border-radius: 5px;
  max-height: 24px;
}

>>> .v-datatable__actions
  .v-text-field
  > .v-input__control
  > .v-input__slot::after,
>>> .v-datatable__actions
  .v-text-field
  > .v-input__control
  > .v-input__slot::before {
  width: 0px;
}
>>> .theme--light .v-datatable__actions .v-btn {
  color: #103a84;
}

.theme--dark .allocation-description {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  text-align: center;
  color: rgba(235, 235, 245, 0.6);
  margin-top: 24px;
}
.theme--dark .headline {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 22px !important;
  line-height: 26px;
  text-align: center;
  color: #e9e9e9;
  margin-top: 48px;
  margin-bottom: 16px;
}
>>> .v-datatable__actions .theme--light.v-icon {
  color: #103a84 !important;
}
>>> .v-datatable__actions .v-input__append-inner {
  margin-top: 0px !important;
}
>>> .theme--dark.v-table {
  background: #212121;
}
>>> .theme--dark.v-datatable .v-datatable__actions {
  background: #212121;
}
>>> .theme--dark.v-datatable thead th {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 12px;
  line-height: 14px;
  color: rgba(235, 235, 245, 0.6);
}
>>> .theme--dark.v-datatable tbody tr td {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: #e9e9e9;
}
>>> .theme--dark.v-datatable tbody tr td .v-btn {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
}
>>> .theme--dark.v-datatable thead th.column.sortable.active,
>>> .theme--dark.v-datatable thead th.column.sortable.active .v-icon,
>>> .theme--dark.v-datatable thead th.column.sortable:hover {
  color: #ffffff;
}
>>> .theme--dark .v-datatable__actions {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 13px;
  line-height: 16px;
  letter-spacing: 0.02em;
  color: rgba(235, 235, 245, 0.6);
}
>>> .theme--dark .v-datatable__actions .v-select__selections {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  max-height: 24px;
  color: rgba(235, 235, 245, 0.6);
}

>>> .theme--dark
  .v-datatable__actions
  .v-select__selection.v-select__selection--comma {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 17px;
  padding-left: 8px;
  color: #ffffff;
}

>>> .theme--dark .v-datatable__actions .v-input__slot {
  border: 0.8px solid #ffffff;
  box-sizing: border-box;
  border-radius: 5px;
  max-height: 24px;
}
>>> .v-datatable__actions .theme--dark.v-icon {
  color: #ffffff !important;
}

.theme--dark span.desc {
  font-family: "Lato", serif !important;
  font-style: normal;
  font-weight: normal;
  font-size: 16px;
  line-height: 19px;
  color: rgba(235, 235, 245, 0.6);
}
>>> .theme--dark
  .v-text-field
  > .v-input__control
  > .v-input__slot
  > .v-text-field__slot {
  background: #303030;
  box-shadow: inset 1px 1px 3px rgba(28, 28, 28, 0.34);
  border-radius: 5px;
  padding-left: 5px;
  padding-right: 5px;
}
>>> .theme--dark.v-table thead tr:first-child {
  border-bottom: 1px solid #303030;
}
>>> .theme--dark.v-table tbody tr:not(:first-child) {
  border-top: 1px solid #303030;
}
.number-input >>> input[type="number"] {
  -moz-appearance: textfield;
}
.number-input >>> input::-webkit-outer-spin-button,
.number-input >>> input::-webkit-inner-spin-button {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
}
.theme--dark .child-slider >>> .v-slider .v-slider__track__container,
.theme--dark .master-slider >>> .v-slider .v-slider__track__container {
  /*noinspection CssUnresolvedCustomProperty*/
  background: linear-gradient(
    to right,
    #ffc929 0%,
    #ffc929 var(--allocation-red-pct, 0%),
    #3455ce var(--allocation-red-pct, 0%),
    #3455ce var(--allocation-blue-pct, 0%),
    #7ec171 var(--allocation-blue-pct, 0%),
    #7ec171 var(--allocation-green-pct, 0%),
    #303030 var(--allocation-green-pct, 0%),
    #303030 100%
  );
  border: 1px solid #424242;
  height: 10px;
  box-shadow: none;
}
@media (max-width: 475px) {
  .between-icons {
    margin-left: 0px !important;
  }
}
@media (max-width: 375px) {
  .theme--light span.desc {
    font-family: "Lato", serif !important;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 19px;
    color: #7395d0;
  }
  .theme--dark span.desc {
    font-family: "Lato", serif !important;
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 19px;
    color: rgba(235, 235, 245, 0.6);
  }
  .desc-icon {
    margin-right: 4px;
    margin-bottom: -4px;
  }

}
</style>

<script>
import { mapGetters } from "vuex";
import ActiveMarketsItemAdvanced from "./ActiveMarketsItemAdvanced";
import { bugsnagClient } from "~/plugins";
import swal from "sweetalert2";
import axios from "axios";
import marketToggler from "../../mixins/marketToggler";

export default {
  components: {
    ActiveMarketsItemAdvanced
  },
  props: ["baseCurrency", "markets", "exchange"],
  data: function() {
    return {
      showSaveChangedBnt: false,
      inputChanged: "input-changed",
      showDataSaved: "input-saved",
      windowWidth: window.innerWidth,
      pagination: {
        descending: false,
        sortBy: "market_name",
        rowsPerPage: 10
      }
    };
  },
  computed: {
    isMobile() {
      return this.windowWidth < 768;
    },
    headers() {
      if (this.windowWidth > 768) {
        return [
          { text: "Market", value: "market_name", sortable: true },
          {
            text: "Target allocation (%)",
            value: "old_allocation",
            sortable: true
          },
          { text: null, value: null, sortable: false },
          { text: "Settings", value: null, sortable: false },
          { text: "Enable/disable", value: "is_activated", sortable: true },
          { text: "Add/remove", value: null, sortable: false }
        ];
      } else {
        return [
          {
            text: "Target allocation (%)",
            value: "old_allocation",
            sortable: false
          }
        ];
      }
    },
    bigSliderValue() {
      const enabledMarkets = this.markets.filter(
        m =>
          m.enabled &&
          m.base_currency === this.baseCurrency &&
          m.exchange === this.exchange
      );

      let returnValue = 0;
      for (let enabledMarket of enabledMarkets) {
        returnValue += enabledMarket.allocation;
      }
      return returnValue <= 100 ? returnValue : 100;
    },
    currentBuysPct() {
      return this.summedBuysPct.hasOwnProperty(this.exchange) &&
        this.summedBuysPct[this.exchange].hasOwnProperty(this.baseCurrency)
        ? this.summedBuysPct[this.exchange][this.baseCurrency]
        : 0;
    },
    currentSellsNoSurplusPct() {
      return this.summedSellsNoSurplusPct.hasOwnProperty(this.exchange) &&
        this.summedSellsNoSurplusPct[this.exchange].hasOwnProperty(
          this.baseCurrency
        )
        ? this.summedSellsNoSurplusPct[this.exchange][this.baseCurrency]
        : 0;
    },
    currentSellsPct() {
      return this.summedSellsPct.hasOwnProperty(this.exchange) &&
        this.summedSellsPct[this.exchange].hasOwnProperty(this.baseCurrency)
        ? this.summedSellsPct[this.exchange][this.baseCurrency]
        : 0;
    },
    allocationLeft: function() {
      let result = {};

      // 100 - allocation
      Object.keys(this.totalAllocations).map((exchange, index) => {
        result[exchange] = [];
        Object.keys(this.totalAllocations[exchange]).map((base, index) => {
          result[exchange][base] = 100 - this.totalAllocations[exchange][base];
        });
      });

      return result;
    },
    summedBuysPct() {
      return this.sumOnBaseCurrencyProperty("fe_in_open_buys_pct");
    },
    summedSellsNoSurplusPct() {
      return this.sumOnBaseCurrencyProperty(
        "fe_reserved_for_selling_no_surplus_pct"
      );
    },
    summedSellsPct() {
      return this.sumOnBaseCurrencyProperty(
        "fe_reserved_for_selling_surplus_pct"
      );
    },
    ...mapGetters({
      totalAllocations: "markets/totalAllocations",
      totalOldAllocations: "markets/totalOldAllocations",
      allocationData: "markets/allocations",
      themeDark: "settings/darkmode"
    })
  },
  created() {
    const allocations = this.$store.getters["markets/allocations"];
    if (allocations.data.length === 0 && allocations.isFetching === false) {
      this.$store.dispatch("markets/fetchAllocations");
    }
  },
  mounted() {
    window.onresize = () => {
      this.windowWidth = window.innerWidth;
    };
  },
  mixins: [marketToggler],
  methods: {
    changeIsActivated: async function(market, market_name, status) {
      try {
        const { data } = await axios.post(
          "/api/v1/markets/" + market_name + "/activate",
          {
            status: status ? 1 : 0
          }
        );
        if (data.status === false) {
          swal({
            title: "Trade engine status error",
            text: "Something went wrong",
            showConfirmButton: false,
            showCloseButton: true,
            customClass: this.themeDark
              ? "swal-type-error-dark"
              : "swal-type-error",
            imageUrl: "/img/popup_img_error.png",
            imageHeight: 80,
            imageWidth: 80
          });
        } else {
          await this.$store.dispatch("markets/fetchMarkets");
        }
      } catch (error) {
        if (error && error.response && error.response.status === 403) {
          await swal({
            title: "Too many markets selected",
            html: `You can not add another market, because you exceeded the markets limit in your subscription.`,
            showConfirmButton: false,
            showCloseButton: true,
            customClass: this.themeDark
              ? "swal-type-error-dark"
              : "swal-type-error",
            imageUrl: "/img/popup_img_error.png",
            imageHeight: 80,
            imageWidth: 80
          });
          market.is_activated = !status;
          await this.$store.dispatch("markets/updateMarket", market);
        } else {
          await swal({
            title: "Market toggle error",
            text: "Please reload the page and try again.",
            showConfirmButton: false,
            showCloseButton: true,
            customClass: this.themeDark
              ? "swal-type-error-dark"
              : "swal-type-error",
            imageUrl: "/img/popup_img_error.png",
            imageHeight: 80,
            imageWidth: 80
          });
        }
      }
    },
    marketDataChange: function(
      market,
      showSaveBtn,
      showLoadingView,
      showDataSaved = false
    ) {
      for (let i = 0; i < this.markets.length; i++) {
        if (this.markets[i].name == market.name) {
          this.$set(this.markets[i], "showSaveBtn", showSaveBtn);
          this.$set(this.markets[i], "showLoadingView", showLoadingView);
          this.$set(this.markets[i], "showDataSaved", showDataSaved);

          if (showDataSaved) {
            setTimeout(
              () => this.$set(this.markets[i], "showDataSaved", false),
              2000
            );
          }
        }
      }
    },
    changeSort: function(header) {
      this.pagination.sortBy = header.value;
      this.pagination.descending = !this.pagination.descending;
    },
    customTableSort(items, index, isDescending) {
      const factor = isDescending ? -1 : 1;

      items
        .sort((a, b) => {
          const aValue = a[index];
          const bValue = b[index];
          if (aValue === bValue) {
            return 0;
          }
          return factor * (aValue < bValue ? -1 : 1);
        })
        .sort((a, b) => {
          if (a.enabled === b.enabled) {
            return 0;
          }
          return a.enabled < b.enabled ? 1 : -1;
        });

      return items;
    },
    sortMarketsOnEnabled(markets) {
      return markets.sort((a, b) => {
        if (a.enabled === b.enabled) {
          return 0;
        }
        return a.enabled < b.enabled ? 1 : -1;
      });
    },
    getBuyPct(exchange, market) {
      const item = this.allocationData.data.find(
        item => item.exchange === exchange && item.market_name === market
      );

      if (item !== undefined) {
        return item.fe_in_open_buys_pct;
      }

      return 0;
    },
    getSellNoSurplusPct(exchange, market) {
      const item = this.allocationData.data.find(
        item => item.exchange === exchange && item.market_name === market
      );

      if (item !== undefined) {
        return item.fe_reserved_for_selling_no_surplus_pct;
      }

      return 0;
    },
    getSellPct(exchange, market) {
      const item = this.allocationData.data.find(
        item => item.exchange === exchange && item.market_name === market
      );

      if (item !== undefined) {
        return item.fe_reserved_for_selling_surplus_pct;
      }

      return 0;
    },
    sumOnBaseCurrencyProperty(property) {
      return this.allocationData.data.reduce((data, item) => {
        if (!data.hasOwnProperty(item.exchange)) {
          data[item.exchange] = {};
        }

        if (!data[item.exchange].hasOwnProperty(item.base_currency)) {
          data[item.exchange][item.base_currency] = 0;
        }

        data[item.exchange][item.base_currency] += item[property];

        return data;
      }, {});
    },
    changedAllocation: function(event, market) {
      this.marketDataChange(market, true, false);
    },
    resetMarket: async function(event, market) {
      await this.$store.dispatch("markets/updateMarketAllocation", {
        exchange: market.exchange,
        baseCurrency: market.base_currency,
        name: market.name,
        pct: market.old_allocation,
        showLoadingView: false,
        showSaveBtn: false,
        refreshOldAllocation: true
      });
    },
    saveAllocation: async function(event, market) {
      let targetAllocation = market.allocation;
      if (isNaN(targetAllocation)) {
        targetAllocation = 0;
      }
      if (
        this.totalOldAllocations.hasOwnProperty(market.exchange) &&
        this.totalOldAllocations[market.exchange].hasOwnProperty(
          market.base_currency
        ) &&
        this.totalOldAllocations[market.exchange][market.base_currency] -
          market.old_allocation +
          market.allocation >
          100
      ) {
        targetAllocation =
          targetAllocation -
          (this.totalOldAllocations[market.exchange][market.base_currency] -
            market.old_allocation +
            market.allocation -
            100);
      }
      this.marketDataChange(market, false, true);

      try {
        const { data } = await axios
          .put(
            "/api/v1/markets/" + market.name + "/percent/" + targetAllocation
          )
          .then(response => {
            if (response.data.pivot.max_investment_pct == targetAllocation) {
              this.$store.dispatch("markets/updateMarketAllocation", {
                exchange: market.exchange,
                baseCurrency: market.base_currency,
                name: market.name,
                pct: response.data.pivot.max_investment_pct,
                showLoadingView: false,
                showSaveBtn: false,
                refreshOldAllocation: true
              });
              market.allocation = response.data.pivot.max_investment_pct;
              this.marketDataChange(market, false, false, true);
            } else {
              this.$store.dispatch("markets/fetchMarkets");
            }
            return true;
          })
          .catch(error => {
            swal({
              title: "Problem saving allocation",
              showConfirmButton: false,
              showCloseButton: true,
              customClass: this.themeDark
                ? "swal-type-error-dark"
                : "swal-type-error",
              imageUrl: "/img/popup_img_error.png",
              imageHeight: 80,
              imageWidth: 80
            });

            this.$store.dispatch("markets/fetchMarkets");
            return false;
          });
      } catch (error) {
        // recover
        this.$store.dispatch("markets/fetchMarkets");

        await swal({
          title: "Problem saving allocation",
          showConfirmButton: false,
          showCloseButton: true,
          customClass: this.themeDark
            ? "swal-type-error-dark"
            : "swal-type-error",
          imageUrl: "/img/popup_img_error.png",
          imageHeight: 80,
          imageWidth: 80
        });

        bugsnagClient.notify(error);
      }
    },
    infoAlert: async function(title, text) {
      await swal({
        title: title,
        html: text,
        showConfirmButton: false,
        showCloseButton: true,
        customClass: this.themeDark ? "swal-type-info-dark" : "swal-type-info"
      });
    }
  }
};
</script>



// WEBPACK FOOTER //
// resources/js/components/markets/ActiveMarkets.vue